<script setup lang="ts">
  import type { SmsTemplateQueryParams } from '@api/query';
  import { useDebounceFn } from '@vueuse/core';
  import { ref } from 'vue';

  import type { SMSTemplateSchema } from '@stuut/api-sdk/models';
  import { Button } from '@ui/components/button';
  import { ConfirmDialog } from '@ui/components/confirm-dialog';
  import { SearchBar } from '@ui/components/input';

  defineProps<{
    isCreatingTemplate: boolean;
    isDeletingTemplates: boolean;
    selectedTemplates: SMSTemplateSchema[];
  }>();

  const emit = defineEmits<{
    (e: 'createTemplate'): void;
    (e: 'deleteSelectedTemplates'): void;
  }>();

  const filters = defineModel<SmsTemplateQueryParams>({ required: true });

  const searchValue = ref(filters.value.search || '');

  const createTemplate = () => {
    emit('createTemplate');
  };

  const deleteSelectedTemplates = () => {
    emit('deleteSelectedTemplates');
  };

  const debouncedSearch = useDebounceFn((searchStr: string) => {
    filters.value.search = searchStr || undefined;
    filters.value.page = 0;
  }, 400);

  const onSearchInput = (event: Event) => {
    const value = (event.target as HTMLInputElement).value;
    searchValue.value = value;
    debouncedSearch(value);
  };
</script>

<template>
  <div class="flex justify-between w-full gap-2">
    <SearchBar
      :model-value="searchValue"
      placeholder="Search by template name"
      class="w-[300px] max-w-xs"
      size="sm"
      @input="onSearchInput"
    />

    <div class="flex gap-2">
      <Button size="sm" :loading="isCreatingTemplate" @click="createTemplate">
        <i class="fa fa-plus" />
        <span> Template </span>
      </Button>

      <ConfirmDialog confirm-text="Delete Templates" @confirm="deleteSelectedTemplates">
        <template #title> Are you sure?</template>
        <template #description> This will delete the selected templates. </template>

        <template #trigger>
          <Button
            variant="outline"
            size="sm"
            :loading="isDeletingTemplates"
            :disabled="selectedTemplates.length === 0"
          >
            <i class="fa fa-trash-alt text-destructive" />
            <span> Delete</span>
          </Button>
        </template>
      </ConfirmDialog>
    </div>
  </div>
</template>
